import {
    type DisplayOutreachStatus,
    type OutreachStatus,
} from '@libraries/typescript/api_client';

// Intentionally excludes 'shift_missing' — there are no response counts
// to summarize when the linked shift is gone in the source EHR.
const RESPONSE_SUMMARY_STATUSES: Array<DisplayOutreachStatus | OutreachStatus> =
    ['in_progress', 'awaiting_confirmation', 'filled', 'unfilled', 'cancelled'];

type OutreachResponseCounts =
    | {
          readonly accepted_count?: number;
          readonly declined_count?: number;
          readonly not_a_fit_count?: number;
          readonly needs_review_count?: number;
          readonly pending_count?: number;
      }
    | null
    | undefined;

function getResponseCount(count: number | undefined): number {
    return count ?? 0;
}

export function getOutreachResponseCountsTotal(
    responseCounts: OutreachResponseCounts,
): number {
    return (
        getResponseCount(responseCounts?.accepted_count) +
        getResponseCount(responseCounts?.pending_count) +
        getResponseCount(responseCounts?.declined_count) +
        getResponseCount(responseCounts?.not_a_fit_count) +
        getResponseCount(responseCounts?.needs_review_count)
    );
}

export function shouldShowOutreachResponseSummary({
    displayStatus,
    isAgentAnalyzing,
}: {
    readonly displayStatus:
        | DisplayOutreachStatus
        | OutreachStatus
        | null
        | undefined;
    readonly isAgentAnalyzing: boolean;
}): boolean {
    if (isAgentAnalyzing || !displayStatus) {
        return false;
    }

    return RESPONSE_SUMMARY_STATUSES.includes(displayStatus);
}

export function OutreachResponseSummary({
    responseCounts,
    displayStatus,
    isAgentAnalyzing,
}: {
    readonly responseCounts: OutreachResponseCounts;
    readonly displayStatus:
        | DisplayOutreachStatus
        | OutreachStatus
        | null
        | undefined;
    readonly isAgentAnalyzing: boolean;
}) {
    if (
        !shouldShowOutreachResponseSummary({
            displayStatus,
            isAgentAnalyzing,
        })
    ) {
        return null;
    }

    const acceptedCount = getResponseCount(responseCounts?.accepted_count);
    const pendingCount = getResponseCount(responseCounts?.pending_count);
    const declinedCount = getResponseCount(responseCounts?.declined_count);
    const notAFitCount = getResponseCount(responseCounts?.not_a_fit_count);
    const needsReviewCount = getResponseCount(
        responseCounts?.needs_review_count,
    );

    return (
        <span className="text-muted-foreground text-xs">
            <span className="text-foreground font-medium">{acceptedCount}</span>{' '}
            yes
            <span className="text-muted-foreground/60"> · </span>
            <span className="text-foreground font-medium">
                {pendingCount}
            </span>{' '}
            pending
            <span className="text-muted-foreground/60"> · </span>
            <span className="text-foreground font-medium">
                {declinedCount}
            </span>{' '}
            no
            <span className="text-muted-foreground/60"> · </span>
            <span className="text-foreground font-medium">
                {notAFitCount}
            </span>{' '}
            not fit
            <span className="text-muted-foreground/60"> · </span>
            <span className="text-foreground font-medium">
                {needsReviewCount}
            </span>{' '}
            review
        </span>
    );
}
